// askAllie.js
// Pure utility functions for SB Assist — no React, no side effects.

// ─── Message ID ───────────────────────────────────────────────────────────────
// Module-level counter keeps IDs unique across re-renders.
let _msgCounter = 0;
export const nextId = () => ++_msgCounter;

// ─── Conversation ─────────────────────────────────────────────────────────────
export const generateConvId = () =>
  `conv_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

/** Insert or replace a conversation in the history list, keyed by id.
 *
 *  Every snapshot of the active chat must be stored under its real backend
 *  session_id when it has one. Storing it under a fresh generateConvId()
 *  instead is what caused conversations to appear twice: the same chat came
 *  back from fetchAllieHistory under its session_id while a local copy sat
 *  alongside it under a random id, and the id-based merge could not match them.
 *  The newest entry always sorts to the front. */
export const upsertConversation = (conversations, conv) => {
  const rest = conversations.filter((c) => c.id !== conv.id);
  return [conv, ...rest];
};

/** Derive a conversation title from its first user message. */
export const generateTitle = (messages) => {
  const first = messages.find((m) => m.role === "user");
  if (!first) return "New Conversation";
  const text = first.content.trim();
  return text.length > 45 ? `${text.slice(0, 42)}…` : text;
};

// ─── Greeting personalization ────────────────────────────────────────────────
/** Replace the {name} placeholder in a greeting template with the user's name.
 *  Falls back to "there" when profileName is empty (e.g. "Hi there 👋"). */
export const personalizeGreeting = (template, profileName) => {
  const name = profileName?.trim() || "";
  return template.replace("{name}", name || "there");
};

// ─── Date formatting ─────────────────────────────────────────────────────────
/** Return a human-friendly relative date string (e.g. "3m ago", "Yesterday"). */
export const formatRelativeDate = (timestamp) => {
  if (!timestamp || isNaN(timestamp)) return "";
  const diffMs = Date.now() - timestamp;
  const mins = Math.floor(diffMs / 60_000);
  const hours = Math.floor(diffMs / 3_600_000);
  const days = Math.floor(diffMs / 86_400_000);

  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days === 1) return "Yesterday";
  return new Date(timestamp).toLocaleDateString();
};

/** Group an array of conversations into date buckets for the history sidebar.
 *  Returns an array of { label, items } objects, omitting empty buckets. */
export const groupByDate = (conversations) => {
  const now = Date.now();
  const startOfToday = new Date(now).setHours(0, 0, 0, 0);
  const startOfYesterday = startOfToday - 86_400_000;
  const startOfThisWeek = startOfToday - 6 * 86_400_000;

  const buckets = [
    { label: "Today", items: [] },
    { label: "Yesterday", items: [] },
    { label: "This week", items: [] },
    { label: "Older", items: [] },
  ];

  for (const conv of conversations) {
    const ts = conv.createdAt || 0;
    if (ts >= startOfToday) buckets[0].items.push(conv);
    else if (ts >= startOfYesterday) buckets[1].items.push(conv);
    else if (ts >= startOfThisWeek) buckets[2].items.push(conv);
    else buckets[3].items.push(conv);
  }

  return buckets.filter((b) => b.items.length > 0);
};
